import { useState, useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { Toggle } from '../components/Toggle'
import { TrashIcon } from '../components/icons/TrashIcon'
import { CpuIcon } from '../components/icons/CpuIcon'
import { InfoNote } from '../components/InfoNote'
import { InfoTooltip } from '../components/InfoTooltip'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useDirty } from '../hooks/useDirty'
import { usePageNavigationGuard } from '../hooks/useUnsavedChangesGuard'
import { hasDirtyOpenEntityEditor, markAllEntityEditorsClean } from '../lib/entityEditorRegistry'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../lib/api'
import { useAuth } from '../auth/useAuth'
import { useSectionAccess, type GlobalTabKey } from '../lib/roleAccess'
import { NotSubscribedCard } from './PreviewPage'
import { RoleMatrixCard } from '../components/RoleMatrixCard'
import { FEATURES } from '../lib/features'
import type {
  GlobalSettingsResponse,
  GlobalModels,
  GlobalSettingsAccess,
  GlobalAccessPermission,
  GlobalPermissionLevel,
  GuardrailConfig,
  GuardrailFormState,
  SkillRef,
  ConnectionBindingRef,
  ConnectionOperationRef,
  KnowledgeSourceRef,
  ModelConfiguration,
  ModelConfigurationListResponse,
  ModelStatus,
  PrincipalSearchItem,
} from '../lib/types'
import { sortAccessPermissions, type AccessSortKey, type AccessSortOrder } from '../lib/accessSort'
import { GuardrailsTab, GlobalGuardrailsPreview } from './GuardrailsTab'
import { GlobalSkillsTab, GlobalSkillsPreview } from './GlobalSkillsTab'
import { GlobalConnectionsTab, ConnectionsPreview } from './GlobalConnectionsTab'
import { GlobalKnowledgeTab, GlobalKnowledgePreview } from './GlobalKnowledgeTab'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog'
import { PageSaveDiscardActions } from '../components/PageSaveDiscardActions'
import { Card } from '../components/Card'
import { Avatar } from '../components/Avatar'
import { AddPrincipalDialog } from '../components/AddPrincipalDialog'
import { Spinner } from '../components/Spinner'
import { PageHeader } from '../components/PageHeader'

/* ─── Tab definition ─── */

type TabId = 'general' | 'models' | 'skills' | 'connections' | 'knowledge' | 'guardrails' | 'access'

const TAB_IDS: TabId[] = ['general', 'models', 'skills', 'connections', 'knowledge', 'guardrails', 'access']

/* URL aliases (R-NAV-8): the `:tab` route segment differs from the internal
 * TabId for two tabs (`knowledge-base`, `studio-access`), matching the
 * prototype's alias table. */
export const TAB_ID_TO_URL: Record<TabId, string> = {
  general: 'general',
  models: 'models',
  skills: 'skills',
  connections: 'connections',
  knowledge: 'knowledge-base',
  guardrails: 'guardrails',
  access: 'studio-access',
}
const TAB_URL_TO_ID: Record<string, TabId> = Object.fromEntries(
  Object.entries(TAB_ID_TO_URL).map(([id, url]) => [url, id as TabId])
)

/* `general` and `models` are deliberately absent from GLOBAL_TAB_ACCESS
 * (see lib/roleAccess.ts) — an id absent from the role map is not role-gated. */
const GLOBAL_TAB_KEYS: readonly TabId[] = ['skills', 'connections', 'knowledge', 'guardrails', 'access']
function isGlobalTabKey(id: TabId): id is GlobalTabKey {
  return (GLOBAL_TAB_KEYS as readonly string[]).includes(id)
}

/** Cache key for the umbrella global-settings GET. Shared so every mutation that
 * changes tenant-wide config can refresh it — the app-wide `staleTime` means a
 * remount would otherwise re-render pre-save data until the window expires. */
const GLOBAL_SETTINGS_QUERY_KEY = ['global-settings'] as const

/* One PUT endpoint per tab (MS-741 §3.1) — replaces the umbrella
 * `PUT /api/v1/global-settings` save. `general` has no endpoint: it is
 * feature-flagged off (`FEATURES.globalSettingsGeneral`) and unreachable. */
const TAB_SAVE_ENDPOINT: Partial<Record<TabId, string>> = {
  models: '/api/v1/global-settings/models',
  skills: '/api/v1/global-settings/skills',
  connections: '/api/v1/global-settings/connections',
  knowledge: '/api/v1/global-settings/knowledge-base',
  guardrails: '/api/v1/global-settings/guardrails',
  access: '/api/v1/global-settings/studio-access',
}

/** Builds the `{version, <one section>}` body for the active tab's save — a Save
 * never carries more than one tab's changes (the page already refuses a tab switch
 * while dirty), so this is always a single-section payload. */
function buildTabSavePayload(tabId: TabId, form: GlobalFormState): Record<string, unknown> {
  const version = form.version
  switch (tabId) {
    case 'models': return { version, models: form.models }
    case 'skills': return { version, skills: form.skills }
    case 'connections': return { version, connections: form.connections }
    case 'knowledge': return { version, knowledgeSources: form.knowledgeSources }
    case 'guardrails': return { version, guardrails: fromGuardrailForm(form.guardrails) }
    case 'access': return { version, access: form.access }
    default: throw new Error(`No save endpoint for tab "${tabId}"`)
  }
}

/* The tab list is filtered by two independent flags (see `TABS` below):
 * `general` is removed entirely when `globalSettingsGeneral` is off, and
 * `models` is hidden-but-present when `globalSettingsModels` is off. "First
 * tab" therefore isn't a constant — this is the single source of truth for
 * it, used by the page's activeTab fallback, its unknown-tab redirect, and
 * routes.tsx's index redirect. */
export function firstGlobalSettingsTabId(): TabId {
  return FEATURES.globalSettingsGeneral ? 'general' : 'skills'
}

export const globalSettingsTabPath = (id: TabId): string => `/global-settings/${TAB_ID_TO_URL[id]}`

/* ─── Form state ─── */

interface GlobalFormState {
  version: number
  guardrails: GuardrailFormState
  access: GlobalSettingsAccess
  skills: SkillRef[]
  connections: ConnectionBindingRef[]
  knowledgeSources: KnowledgeSourceRef[]
  models: GlobalModels
}

function toGuardrailForm(g: GuardrailConfig): GuardrailFormState {
  return {
    ...g,
    monetaryApprovalThreshold: g.monetaryApprovalThreshold ?? '',
    monetaryApprovalCurrency: g.monetaryApprovalCurrency ?? 'EUR',
  }
}

function fromGuardrailForm(f: GuardrailFormState): GuardrailConfig {
  // Threshold + currency must both be set or both null per the backend
  // validator — sending currency without a threshold (or vice versa) 400s
  // the entire save, which silently kills the rest of the payload too.
  const threshold = f.monetaryApprovalThreshold.trim() || null
  return {
    ...f,
    monetaryApprovalThreshold: threshold,
    monetaryApprovalCurrency: threshold ? (f.monetaryApprovalCurrency || null) : null,
  }
}

/* Placeholder values for the four Admin-only sections an Editor's GET withholds
 * (`null`, per MS-741 §3.1 GET scoping — see the GlobalSettingsResponse doc
 * comment in lib/types.ts). These exist ONLY to keep `GlobalFormState` a plain,
 * always-populated shape internally; the components that would render them are
 * gated off entirely for a caller who can't see the real section (below), so
 * these placeholders are never displayed or saved. */
const EMPTY_GUARDRAIL_FORM: GuardrailFormState = {
  requireHumanConfirmation: false,
  escalateOnUncertainty: false,
  monetaryApprovalThreshold: '',
  monetaryApprovalCurrency: 'EUR',
  allowBulkOperations: false,
  allowOutOfHoursActions: false,
  dryRunMode: false,
  sessionTimeoutMinutes: 30,
  restrictToAgentScope: true,
  showConfidenceScores: true,
  showDataSourceCitations: true,
  allowInternetAccess: true,
  responseLanguage: 'und',
}
const EMPTY_ACCESS: GlobalSettingsAccess = { permissions: [] }
const EMPTY_MODELS: GlobalModels = { defaultModelId: '', availability: [] }

/** Applies a per-tab PUT response to form state: the shared `version` always,
 * plus ONLY the section that was just saved.
 *
 * Never re-initialise the whole form from a per-tab response. The response is
 * built at the caller's own level, so a section this tab did not save may come
 * back `null` — meaning "withheld from this caller", which `initForm` turns into
 * an `EMPTY_*` placeholder. Doing that here would replace an Admin's real
 * guardrails/access/models with placeholders after a Skills save, and the next
 * save on one of those tabs would then write the placeholder to the tenant.
 * Only the active tab can be dirty (the page refuses a tab switch mid-edit), so
 * carrying every other section over from `prev` loses nothing. */
function mergeTabSaveResponse(
  prev: GlobalFormState,
  tabId: TabId,
  data: GlobalSettingsResponse
): GlobalFormState {
  const next: GlobalFormState = { ...prev, version: data.version }
  switch (tabId) {
    case 'models': if (data.models) next.models = data.models; break
    case 'skills': next.skills = data.skills; break
    case 'connections': if (data.connections) next.connections = data.connections; break
    case 'knowledge': next.knowledgeSources = data.knowledgeSources; break
    case 'guardrails': if (data.guardrails) next.guardrails = toGuardrailForm(data.guardrails); break
    case 'access': if (data.access) next.access = data.access; break
    default: break
  }
  return next
}

function initForm(data: GlobalSettingsResponse): GlobalFormState {
  return {
    version: data.version,
    guardrails: data.guardrails ? toGuardrailForm(data.guardrails) : EMPTY_GUARDRAIL_FORM,
    access: data.access ?? EMPTY_ACCESS,
    skills: data.skills,
    connections: data.connections ?? [],
    knowledgeSources: data.knowledgeSources,
    models: data.models ?? EMPTY_MODELS,
  }
}

function sortedJSON(v: unknown): string {
  return JSON.stringify(v, Object.keys(v as object).sort())
}

function isDirty(form: GlobalFormState, saved: GlobalFormState): boolean {
  const f = { ...form, guardrails: sortedJSON(form.guardrails), skills: [...form.skills].sort((a, b) => a.skillId.localeCompare(b.skillId)), connections: [...form.connections].sort((a, b) => a.connectionId.localeCompare(b.connectionId)), knowledgeSources: [...form.knowledgeSources].sort((a, b) => a.knowledgeSourceId.localeCompare(b.knowledgeSourceId)) }
  const s = { ...saved, guardrails: sortedJSON(saved.guardrails), skills: [...saved.skills].sort((a, b) => a.skillId.localeCompare(b.skillId)), connections: [...saved.connections].sort((a, b) => a.connectionId.localeCompare(b.connectionId)), knowledgeSources: [...saved.knowledgeSources].sort((a, b) => a.knowledgeSourceId.localeCompare(b.knowledgeSourceId)) }
  return JSON.stringify(f) !== JSON.stringify(s)
}

/* ─── Risky change detection ─── */

interface RiskyChanges {
  dryRunOff: boolean
  skillsDisabled: string[]
  connectionsDisabled: string[]
  modelsDeactivated: string[]
}

function detectRiskyChanges(saved: GlobalFormState, form: GlobalFormState): RiskyChanges {
  const dryRunOff = saved.guardrails.dryRunMode && !form.guardrails.dryRunMode

  const skillsDisabled = form.skills
    .filter(s => saved.skills.find(ss => ss.skillId === s.skillId)?.isEnabled === true && !s.isEnabled)
    .map(s => s.skillId)

  const connectionsDisabled = form.connections
    .filter(c => saved.connections.find(sc => sc.connectionId === c.connectionId)?.isEnabled === true && !c.isEnabled)
    .map(c => c.connectionId)

  const modelsDeactivated = form.models.availability
    .filter(a => saved.models.availability.find(sm => sm.modelId === a.modelId)?.status === 'Active' && a.status === 'Inactive')
    .map(a => a.modelId)

  return { dryRunOff, skillsDisabled, connectionsDisabled, modelsDeactivated }
}

function hasRiskyChanges(r: RiskyChanges): boolean {
  return r.dryRunOff || r.skillsDisabled.length > 0 || r.connectionsDisabled.length > 0 || r.modelsDeactivated.length > 0
}

function validateModels(models: GlobalModels): 'globalSettings.errors.oneModelRequired' | 'globalSettings.errors.defaultModelMustBeActive' | null {
  const active = models.availability.filter(a => a.status === 'Active')
  if (active.length === 0) return 'globalSettings.errors.oneModelRequired'
  if (!active.some(a => a.modelId === models.defaultModelId)) return 'globalSettings.errors.defaultModelMustBeActive'
  return null
}

function buildRiskyMessage(t: TFunction<'translation', undefined>, r: RiskyChanges): string {
  const lines: string[] = []
  if (r.dryRunOff) lines.push(`• ${t('globalSettings.riskyChanges.dryRunDisabled', { count: 1 })}`)
  if (r.skillsDisabled.length > 0) lines.push(`• ${t('globalSettings.riskyChanges.skillsDisabled', { count: r.skillsDisabled.length })}`)
  if (r.connectionsDisabled.length > 0) lines.push(`• ${t('globalSettings.riskyChanges.connectionsDisabled', { count: r.connectionsDisabled.length })}`)
  if (r.modelsDeactivated.length > 0) lines.push(`• ${t('globalSettings.riskyChanges.modelsDeactivated', { count: r.modelsDeactivated.length })}`)
  return lines.join('\n')
}

/* ─── Shared UI primitives ─── */

const labelStyle: React.CSSProperties = {
  display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: 'var(--text-primary)',
}

/* ─── Models tab ─── */

interface ModelsTabProps {
  models: GlobalModels
  catalog: ModelConfiguration[]
  onChange: (models: GlobalModels) => void
}

function ModelsTab({ models, catalog, onChange }: ModelsTabProps) {
  const { t } = useTranslation()
  const modelErrorKey = validateModels(models)
  const modelError = modelErrorKey ? t(modelErrorKey) : null
  const [disableModelConfirm, setDisableModelConfirm] = useState<ModelConfiguration | null>(null)
  const [setDefaultConfirm, setSetDefaultConfirm] = useState<ModelConfiguration | null>(null)
  const [selectNewDefaultFor, setSelectNewDefaultFor] = useState<ModelConfiguration | null>(null)
  const [newDefaultSelection, setNewDefaultSelection] = useState<string>('')

  useEffect(() => {
    if (!selectNewDefaultFor) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { setSelectNewDefaultFor(null); setNewDefaultSelection('') } }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [selectNewDefaultFor])

  function getStatus(modelId: string): ModelStatus {
    return models.availability.find(a => a.modelId === modelId)?.status ?? 'Active'
  }

  function setStatus(modelId: string, status: ModelStatus) {
    const next = models.availability.map(a => a.modelId === modelId ? { ...a, status } : a)
    if (!next.some(a => a.modelId === modelId)) {
      next.push({ modelId, status })
    }
    onChange({ ...models, availability: next })
  }

  function setDefault(modelId: string) {
    onChange({ ...models, defaultModelId: modelId })
  }

  return (
    <Card title={t('globalSettings.models.title')} subtitle={t('globalSettings.models.subtitle')} className="editor-split-card mb-16" noPadding>
      <div className="editor-split-body">
        {/* Left: model list */}
        <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>
          {modelError && (
            <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12, padding: '8px 12px', background: 'var(--danger-bg)', borderRadius: 6 }}>
              {modelError}
            </div>
          )}
          {catalog.length === 0 && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
              {t('globalSettings.models.none')}
            </div>
          )}
          {catalog.map(model => {
            const status = getStatus(model.id)
            const isDefault = models.defaultModelId === model.id
            const isActive = status === 'Active'
            return (
              <div key={model.id} className="skill-row">
                <Toggle
                  checked={isActive}
                  onChange={(v) => {
                    if (!v) {
                      if (model.id === models.defaultModelId) {
                        setSelectNewDefaultFor(model)
                        setNewDefaultSelection('')
                      } else {
                        setDisableModelConfirm(model)
                      }
                    } else {
                      setStatus(model.id, 'Active')
                    }
                  }}
                  label={t('globalSettings.models.toggleAvailability', { name: model.modelName })}
                />
                <div style={{ flex: 1, minWidth: 0, opacity: isActive ? 1 : 0.55 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {model.modelName}
                    <span className="badge badge-global" style={{ padding: '2px 8px', fontSize: 10 }}>{model.provider}</span>
                    {isDefault && (
                      <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: 'var(--bg-badge)', color: 'var(--text-secondary)' }}>{t('globalSettings.models.default')}</span>
                    )}
                  </div>
                </div>
                {isActive && !isDefault && (
                  <button type="button" onClick={() => setSetDefaultConfirm(model)}
                    style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', background: 'transparent', color: 'var(--text-tertiary)', border: '1px solid var(--border)' }}>
                    {t('globalSettings.models.setDefault')}
                  </button>
                )}
              </div>
            )
          })}
        </div>

        {/* Separator */}
        <div style={{ width: 1, background: 'var(--border-light)', margin: '20px 0' }} />

        {/* Right: info pane */}
        <div className="editor-pane-col">
          <div className="editor-pane-empty">
            <CpuIcon size={32} />
            <p style={{ fontSize: 13 }}>{t('globalSettings.models.editingNotSupported')}</p>
            <p style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center', maxWidth: 280 }}>
              {t('globalSettings.models.managedBy')}
            </p>
          </div>
        </div>
      </div>
      {disableModelConfirm && (
        <ConfirmDialog
          title={t('globalSettings.models.disableDialog.title', { name: disableModelConfirm.modelName })}
          message={t('globalSettings.models.disableDialog.message')}
          confirmLabel={t('globalSettings.models.disableDialog.confirm')}
          danger
          onConfirm={() => { setStatus(disableModelConfirm.id, 'Inactive'); setDisableModelConfirm(null) }}
          onCancel={() => setDisableModelConfirm(null)}
        />
      )}
      {setDefaultConfirm && (
        <ConfirmDialog
          title={t('globalSettings.models.setDefaultDialog.title', { name: setDefaultConfirm.modelName })}
          message={t('globalSettings.models.setDefaultDialog.message')}
          confirmLabel={t('globalSettings.models.setDefaultDialog.confirm')}
          onConfirm={() => { setDefault(setDefaultConfirm.id); setSetDefaultConfirm(null) }}
          onCancel={() => setSetDefaultConfirm(null)}
        />
      )}
      {selectNewDefaultFor && (() => {
        const options = catalog.filter(m => m.id !== selectNewDefaultFor.id && getStatus(m.id) === 'Active')
        return (
          <dialog
              open
              aria-modal="true"
              aria-label={t('globalSettings.models.selectNewDefaultDialog.title', { name: selectNewDefaultFor.modelName })}
              className="modal-backdrop modal"
              style={{ width: 440 }}
            >
              <div className="modal-header">
                <h3>{t('globalSettings.models.selectNewDefaultDialog.title', { name: selectNewDefaultFor.modelName })}</h3>
              </div>
              <div className="modal-body">
                <p>{t('globalSettings.models.selectNewDefaultDialog.message')}</p>
                <label htmlFor="select-new-default" className="form-label">
                  {t('globalSettings.models.selectNewDefaultDialog.selectLabel')}
                </label>
                <select
                  id="select-new-default"
                  value={newDefaultSelection}
                  onChange={(e) => setNewDefaultSelection(e.target.value)}
                  disabled={options.length === 0}
                  className="form-select"
                  style={{ width: '100%' }}
                >
                  <option value="">— {t('globalSettings.models.selectNewDefaultDialog.selectPlaceholder')} —</option>
                  {options.map(m => <option key={m.id} value={m.id}>{m.modelName}</option>)}
                </select>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => { setSelectNewDefaultFor(null); setNewDefaultSelection('') }}>
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={!newDefaultSelection}
                  style={{ opacity: newDefaultSelection ? 1 : 0.5 }}
                  onClick={() => {
                    if (!newDefaultSelection) return
                    const next = models.availability.map(a =>
                      a.modelId === selectNewDefaultFor.id ? { ...a, status: 'Inactive' as ModelStatus } : a
                    )
                    if (!next.some(a => a.modelId === selectNewDefaultFor.id)) {
                      next.push({ modelId: selectNewDefaultFor.id, status: 'Inactive' as ModelStatus })
                    }
                    onChange({ ...models, defaultModelId: newDefaultSelection, availability: next })
                    setSelectNewDefaultFor(null)
                    setNewDefaultSelection('')
                  }}
                >
                  {t('globalSettings.models.selectNewDefaultDialog.confirm')}
                </button>
              </div>
            </dialog>
        )
      })()}
    </Card>
  )
}

/* ─── Global Access card ─── */


function formatGrantDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/* Search/sort state for the Studio Access card's principal list. */
function useAccessFilter<T extends { principalName: string; principalEmail: string | null; grantedAt: string | null }>(items: T[]) {
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<AccessSortKey>('user')
  const [sortOrder, setSortOrder] = useState<AccessSortOrder>('asc')
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    const matched = q
      ? items.filter(p => p.principalName.toLowerCase().includes(q) || (p.principalEmail ?? '').toLowerCase().includes(q))
      : items
    return sortAccessPermissions(matched, sortBy, sortOrder)
  }, [items, search, sortBy, sortOrder])
  return { search, setSearch, sortBy, setSortBy, sortOrder, setSortOrder, visible }
}

interface AccessFilterBarProps {
  search: string
  sortBy: AccessSortKey
  sortOrder: AccessSortOrder
  onSearch: (v: string) => void
  onSortBy: (v: AccessSortKey) => void
  onSortOrder: (v: AccessSortOrder) => void
}

function AccessFilterBar({ search, sortBy, sortOrder, onSearch, onSortBy, onSortOrder }: Readonly<AccessFilterBarProps>) {
  const { t } = useTranslation()
  return (
    <div className="filter-fields" style={{ marginBottom: 20 }}>
      <div className="filter-field" style={{ flex: '1 1 220px' }}>
        <label>{t('globalSettings.access.search.label')}</label>
        <input
          type="text"
          className="form-input"
          placeholder={t('globalSettings.access.search.placeholder')}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>
      <div className="filter-field" style={{ flex: '0 0 150px' }}>
        <label>{t('globalSettings.access.search.sortLabel')}</label>
        <select className="form-select" value={sortBy} onChange={(e) => onSortBy(e.target.value as AccessSortKey)}>
          <option value="user">{t('globalSettings.access.search.sortUser')}</option>
          <option value="email">{t('globalSettings.access.search.sortEmail')}</option>
          <option value="added">{t('globalSettings.access.search.sortAdded')}</option>
        </select>
      </div>
      <div className="filter-field" style={{ flex: '0 0 150px' }}>
        <label>{t('globalSettings.access.search.orderLabel')}</label>
        <select className="form-select" value={sortOrder} onChange={(e) => onSortOrder(e.target.value as AccessSortOrder)}>
          <option value="asc">{t('globalSettings.access.search.orderAsc')}</option>
          <option value="desc">{t('globalSettings.access.search.orderDesc')}</option>
        </select>
      </div>
    </div>
  )
}

interface AccessCardShellProps {
  title: string
  subtitle: string
  addLabel: string
  onAddClick: () => void
  children: ReactNode
}

function AccessCardShell({ title, subtitle, addLabel, onAddClick, children }: Readonly<AccessCardShellProps>) {
  return (
    <Card
      title={title}
      subtitle={subtitle}
      actions={<button type="button" onClick={onAddClick} className="btn btn-ghost" style={{ fontSize: 12 }}>{addLabel}</button>}
      className="mb-16"
    >
      {children}
    </Card>
  )
}

/* Empty-state + table-header shell for the Studio Access card below. */
interface AccessTableShellProps {
  isEmpty: boolean
  emptyLabel: string
  userGroupLabel: string
  permissionLabel: string
  addedLabel: string
  permTooltip: ReactNode
  tableId?: string
  children: ReactNode
}

function AccessTableShell({
  isEmpty,
  emptyLabel,
  userGroupLabel,
  permissionLabel,
  addedLabel,
  permTooltip,
  tableId,
  children,
}: Readonly<AccessTableShellProps>) {
  if (isEmpty) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
        {emptyLabel}
      </div>
    )
  }
  return (
    <table className="data-table" id={tableId}>
      <thead>
        <tr>
          <th>{userGroupLabel}</th>
          <th style={{ position: 'relative' }}>
            {permissionLabel}
            <span style={{ marginLeft: 4, display: 'inline-flex' }}>
              <InfoTooltip width={320} placement="below" color="var(--info)">
                {permTooltip}
              </InfoTooltip>
            </span>
          </th>
          <th>{addedLabel}</th>
          <th />
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}

interface GlobalAccessCardProps {
  access: GlobalSettingsAccess
  onChange: (access: GlobalSettingsAccess) => void
  /** Without a Mambu Agents subscription, Editor is not an assignable role
   * (MS-741 AC4 MCP-only): omitted from the add dialog and from any row not
   * already Editor; an existing Editor row is greyed with its select disabled. */
  agentsContracted: boolean
}

function GlobalAccessCard({ access, onChange, agentsContracted }: Readonly<GlobalAccessCardProps>) {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogPermission, setDialogPermission] = useState<GlobalPermissionLevel>('Editor')
  const { search, setSearch, sortBy, setSortBy, sortOrder, setSortOrder, visible: visiblePermissions } = useAccessFilter(access.permissions)

  const adminCount = access.permissions.filter(p => p.permissionLevel === 'Admin').length
  const existingIds = access.permissions.map(p => p.principalId)

  function openDialog() { setDialogPermission(agentsContracted ? 'Editor' : 'Admin'); setDialogOpen(true) }
  function closeDialog() { setDialogOpen(false) }

  function handleAdd(principal: PrincipalSearchItem) {
    const grant: GlobalAccessPermission = {
      principalId: principal.principalId,
      principalType: principal.principalType,
      principalName: principal.principalName,
      principalEmail: principal.principalEmail,
      permissionLevel: dialogPermission,
      grantedAt: new Date().toISOString(),
    }
    onChange({ permissions: [...access.permissions, grant] })
    closeDialog()
  }

  function handleRoleChange(principalId: string, level: GlobalPermissionLevel) {
    onChange({ permissions: access.permissions.map(p => p.principalId === principalId ? { ...p, permissionLevel: level } : p) })
  }

  function handleRemove(principalId: string) {
    onChange({ permissions: access.permissions.filter(p => p.principalId !== principalId) })
  }

  // AC4.14: the Editor line drops out of the permission tooltip when Agents is not
  // contracted — Editor is not an available role then.
  const permTooltip = (
    <>
      {t('globalSettings.permTooltipAdmin')}
      {agentsContracted && (
        <>
          <br />
          {t('globalSettings.permTooltipEditor')}
        </>
      )}
    </>
  )

  return (
    <>
      <AccessCardShell
        title={t('globalSettings.access.title')}
        subtitle={t('globalSettings.access.subtitle')}
        addLabel={t('globalSettings.access.addUserOrGroup')}
        onAddClick={openDialog}
      >
          <AccessFilterBar
            search={search} sortBy={sortBy} sortOrder={sortOrder}
            onSearch={setSearch} onSortBy={setSortBy} onSortOrder={setSortOrder}
          />

          <AccessTableShell
            isEmpty={access.permissions.length === 0}
            emptyLabel={t('globalSettings.access.noGrants')}
            userGroupLabel={t('globalSettings.access.table.userGroup')}
            permissionLabel={t('globalSettings.access.table.permission')}
            addedLabel={t('globalSettings.access.table.added')}
            permTooltip={permTooltip}
            tableId="global-access-table"
          >
            {visiblePermissions.map((p) => {
              const isLastAdmin = p.permissionLevel === 'Admin' && adminCount === 1
              // AC4 MCP-only: without Agents, Editor is not an assignable role — a
              // non-Editor row can't switch to it, but an existing Editor row keeps
              // its own value so the select still renders it (agent-studio.html:6972-7008).
              const isEditorUnavailable = !agentsContracted && p.permissionLevel === 'Editor'
              const editorOptionAllowed = agentsContracted || p.permissionLevel === 'Editor'
              return (
                <tr key={p.principalId} className={isEditorUnavailable ? 'role-unavailable' : undefined}>
                  <td>
                    <div className="access-user">
                      <Avatar className="access-avatar access-avatar-user" size={15} />
                      <div>
                        <div className="access-name">{p.principalName}</div>
                        <div className="access-email">{p.principalType === 'Group' ? 'Group' : (p.principalEmail ?? '')}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <select className="role-select" aria-label={t('globalSettings.access.permissionAriaLabel', { name: p.principalName })} value={p.permissionLevel} disabled={isLastAdmin || isEditorUnavailable}
                      onChange={(e) => handleRoleChange(p.principalId, e.target.value as GlobalPermissionLevel)}
                      title={isLastAdmin ? t('globalSettings.access.promoteAnotherAdminFirst') : undefined}
                      style={{ opacity: isLastAdmin ? 0.6 : 1, cursor: isLastAdmin ? 'not-allowed' : 'default' }}>
                      <option value="Admin">Admin</option>
                      {editorOptionAllowed && <option value="Editor">Editor</option>}
                    </select>
                    {isEditorUnavailable && (
                      <span style={{ marginLeft: 6, display: 'inline-flex' }}>
                        <InfoTooltip width={250} placement="above" color="var(--info)">
                          {t('globalSettings.access.editorUnavailableTooltip')}
                        </InfoTooltip>
                      </span>
                    )}
                  </td>
                  <td className="text-sm text-muted">{formatGrantDate(p.grantedAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="skill-remove-btn"
                      title={isLastAdmin ? t('globalSettings.access.promoteAnotherAdminFirst') : t('globalSettings.access.removeAriaLabel', { name: p.principalName })}
                      aria-label={t('globalSettings.access.removeAriaLabel', { name: p.principalName })}
                      disabled={isLastAdmin}
                      style={{ opacity: isLastAdmin ? 0.35 : undefined, pointerEvents: isLastAdmin ? 'none' : undefined }}
                      onClick={() => handleRemove(p.principalId)}
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              )
            })}
          </AccessTableShell>
      </AccessCardShell>

      {/* Add dialog */}
      {dialogOpen && (
        <AddPrincipalDialog
          typeSelectId="gs-add-type"
          excludeIds={existingIds}
          labelStyle={labelStyle}
          labels={{
            title: t('globalSettings.access.dialog.title'),
            subtitle: t('globalSettings.access.dialog.subtitle'),
            typeLabel: t('globalSettings.access.dialog.typeLabel'),
            searchUsersLabel: t('globalSettings.access.dialog.searchUsersLabel'),
            searchGroupsLabel: t('globalSettings.access.dialog.searchGroupsLabel'),
            clearSelection: t('globalSettings.access.dialog.clearSelection'),
            cancel: t('globalSettings.access.dialog.cancel'),
            add: t('globalSettings.access.dialog.add'),
          }}
          renderAvatar={() => <Avatar className="access-avatar access-avatar-user" size={13} />}
          extraFields={
            <div>
              <label htmlFor="gs-add-permission" style={labelStyle}>{t('globalSettings.access.dialog.permissionLabel')}</label>
              <select id="gs-add-permission" value={dialogPermission} onChange={(e) => setDialogPermission(e.target.value as GlobalPermissionLevel)} className="form-select">
                <option value="Admin">Admin</option>
                {agentsContracted && <option value="Editor">Editor</option>}
              </select>
            </div>
          }
          onCancel={closeDialog}
          onAdd={handleAdd}
        />
      )}
    </>
  )
}

/* ─── Tab button — owns the dirty/entity-editor switch-guard branching so the
   main component's tab bar reduces to a single map() call. ─── */

interface GlobalSettingsTabButtonProps {
  tab: { id: TabId; label: string; previewBadge?: boolean; hidden?: boolean }
  isActive: boolean
  dirty: boolean
  onCommit: (tabId: TabId) => void
  onEntityConflict: (tabId: TabId) => void
  onPageConflict: (tabId: TabId) => void
  previewTooltip: string
  previewLabel: string
}

function GlobalSettingsTabButton({
  tab,
  isActive,
  dirty,
  onCommit,
  onEntityConflict,
  onPageConflict,
  previewTooltip,
  previewLabel,
}: Readonly<GlobalSettingsTabButtonProps>) {
  const ref = useRef<HTMLButtonElement>(null)

  // The tab bar scrolls horizontally on narrow windows; keep the active tab visible
  // (e.g. when deep-linking to the last tab). Optional call: jsdom has no scrollIntoView.
  useEffect(() => {
    if (isActive) ref.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [isActive])

  function handleClick() {
    if (isActive) return
    if (hasDirtyOpenEntityEditor()) { onEntityConflict(tab.id); return }
    if (dirty) { onPageConflict(tab.id); return }
    onCommit(tab.id)
  }

  return (
    <button
      ref={ref}
      type="button"
      aria-selected={isActive}
      role="tab"
      onClick={handleClick}
      style={tab.hidden ? { display: 'none' } : undefined}
      className={`tab-btn${isActive ? ' active' : ''}`}>
      {tab.label}
      {tab.previewBadge && (
        <>
          {' '}
          <span className="nav-badge-soon" title={previewTooltip}>
            {previewLabel}
          </span>
        </>
      )}
    </button>
  )
}

function tabPanelClass(tabId: TabId, activeTab: TabId): string {
  return `tab-panel${activeTab === tabId ? ' active' : ''}`
}

/* Agents preview card, in place of a tab's panel content when the tenant has no
 * active Mambu Agents subscription (MS-741 AC2.4) — the tab button itself stays
 * visible. Mirrors the prototype's `agentsPreviewCardHTML()` (agent-studio.html:7017-7029). */
function AgentsNotSubscribedCard() {
  const { t } = useTranslation()
  return (
    <NotSubscribedCard
      cardTitle={t('previewPage.agents.cardTitle')}
      description={t('previewPage.agents.description')}
      voteFeature="Mambu Agents"
      badgeText={t('preview.agentsContactBadge')}
    />
  )
}

interface GlobalConnectionsPanelProps {
  agentsContracted: boolean
  connectionBindings: ConnectionBindingRef[]
  savedBindings: ConnectionBindingRef[]
  onChange: (connections: ConnectionBindingRef[]) => void
  onAdded: (binding: ConnectionBindingRef) => void
  onRemoved: (connId: string) => void
}

/* Connections panel body — picks between the not-subscribed card, the feature-
 * flagged real tab, and the preview placeholder. A component rather than a
 * ternary chain inline in the panel markup. */
function GlobalConnectionsPanel({
  agentsContracted,
  connectionBindings,
  savedBindings,
  onChange,
  onAdded,
  onRemoved,
}: Readonly<GlobalConnectionsPanelProps>) {
  if (!agentsContracted) return <AgentsNotSubscribedCard />
  if (!FEATURES.globalConnectionsConfig) return <ConnectionsPreview />
  return (
    <GlobalConnectionsTab
      connectionBindings={connectionBindings}
      savedBindings={savedBindings}
      onConnectionToggle={(connId, isEnabled) => onChange(
        connectionBindings.map(c => c.connectionId === connId ? { ...c, isEnabled } : c)
      )}
      onOperationToggle={(connId, opId, isEnabled) => onChange(
        connectionBindings.map(c =>
          c.connectionId === connId
            ? { ...c, operations: c.operations.map((o: ConnectionOperationRef) => o.operationId === opId ? { ...o, isEnabled } : o) }
            : c
        )
      )}
      onAdded={onAdded}
      onRemoved={onRemoved}
    />
  )
}

interface GlobalKnowledgePanelProps {
  agentsContracted: boolean
  onAdded: (kbId: string) => void
  onRemoved: (kbId: string) => void
}

/* Knowledge Base panel body — same not-subscribed / preview / real-tab
 * precedence as GlobalConnectionsPanel above (AC-4). */
function GlobalKnowledgePanel({ agentsContracted, onAdded, onRemoved }: Readonly<GlobalKnowledgePanelProps>) {
  if (!agentsContracted) return <AgentsNotSubscribedCard />
  if (!FEATURES.globalKnowledgeConfig) return <GlobalKnowledgePreview />
  return <GlobalKnowledgeTab onAdded={onAdded} onRemoved={onRemoved} />
}

interface GlobalSkillsPanelProps {
  agentsContracted: boolean
  skillBindings: SkillRef[]
  savedBindings: SkillRef[]
  onToggle: (skillId: string, isEnabled: boolean) => void
  onAdded: (skillId: string) => void
  onRemoved: (skillId: string) => void
}

/* Skills panel body — same not-subscribed / preview / real-tab precedence as
 * GlobalConnectionsPanel above (AC-4). */
function GlobalSkillsPanel({
  agentsContracted,
  skillBindings,
  savedBindings,
  onToggle,
  onAdded,
  onRemoved,
}: Readonly<GlobalSkillsPanelProps>) {
  if (!agentsContracted) return <AgentsNotSubscribedCard />
  if (!FEATURES.globalSkillsConfig) return <GlobalSkillsPreview />
  return (
    <GlobalSkillsTab
      skillBindings={skillBindings}
      savedBindings={savedBindings}
      onToggle={onToggle}
      onAdded={onAdded}
      onRemoved={onRemoved}
    />
  )
}

interface GlobalGuardrailsPanelProps {
  agentsContracted: boolean
  form: GuardrailFormState
  onChange: (patch: Partial<GuardrailFormState>) => void
}

/* Guardrails panel body — same not-subscribed / preview / real-tab
 * precedence as GlobalConnectionsPanel above (AC-4). The info banner is part
 * of "the real tab" case only. */
function GlobalGuardrailsPanel({ agentsContracted, form, onChange }: Readonly<GlobalGuardrailsPanelProps>) {
  const { t } = useTranslation()
  if (!agentsContracted) return <AgentsNotSubscribedCard />
  if (!FEATURES.globalGuardrailsConfig) return <GlobalGuardrailsPreview />
  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <InfoNote>
          <div>{t('globalSettings.guardrails.infoBanner')}</div>
        </InfoNote>
      </div>
      <GuardrailsTab form={form} onChange={onChange} />
    </>
  )
}

/* Keeps the URL's :tab segment legal for this caller: an unrecognised segment
 * falls back to the first tab, and a deep link to a role-hidden Global Settings
 * tab (MS-741 AC3.8) routes to Home — not to the first Global Settings tab,
 * since the whole surface may be role-hidden for this caller too. */
function useGlobalSettingsTabRedirect(tabParam: string | undefined) {
  const navigate = useNavigate()
  const { canOpenGlobalTab, isRoleResolved } = useSectionAccess()
  useEffect(() => {
    if (tabParam && !TAB_URL_TO_ID[tabParam]) {
      navigate(globalSettingsTabPath(firstGlobalSettingsTabId()), { replace: true })
      return
    }
    // Never decide a role-based redirect on an unresolved role — see
    // SectionAccess.isRoleResolved.
    if (!isRoleResolved) return
    const id = TAB_URL_TO_ID[tabParam ?? '']
    if (id && isGlobalTabKey(id) && !canOpenGlobalTab(id)) {
      navigate('/home', { replace: true })
    }
  }, [tabParam, navigate, canOpenGlobalTab, isRoleResolved])
}

/* ─── Main GlobalSettings component ─── */

export function GlobalSettings() {
  const { t } = useTranslation()
  const apiFetch = useApiFetch()
  const navigate = useNavigate()
  const { agentsContracted, isGlobalAdmin } = useAuth()
  const { canOpenGlobalTab } = useSectionAccess()
  const { tab: tabParam } = useParams<{ tab: string }>()
  /* activeTab is derived from the URL's :tab segment, not local state — see
     AgentDetail.tsx for the identical contract (MS-740). Falls back to the
     first tab for an unrecognised segment. */
  const activeTab: TabId = TAB_URL_TO_ID[tabParam ?? ''] ?? firstGlobalSettingsTabId()
  function commitTab(tabId: TabId) {
    navigate(globalSettingsTabPath(tabId))
  }
  useGlobalSettingsTabRedirect(tabParam)
  const [form, setForm] = useState<GlobalFormState | null>(null)
  const [savedForm, setSavedForm] = useState<GlobalFormState | null>(null)
  const [saveConfirm, setSaveConfirm] = useState(false)
  const [pendingTab, setPendingTab] = useState<TabId | null>(null)
  const [entityTabSwitchConfirm, setEntityTabSwitchConfirm] = useState(false)
  const [tabSwitchDialog, setTabSwitchDialog] = useState(false)
  const [tabToSwitchAfterSave, setTabToSwitchAfterSave] = useState<TabId | null>(null)

  /* ─── Fetch global settings ─── */
  const qc = useQueryClient()
  const settingsQuery = useQuery({
    queryKey: GLOBAL_SETTINGS_QUERY_KEY,
    queryFn: () => apiFetch<GlobalSettingsResponse>('/api/v1/global-settings'),
  })

  const TAB_LABEL_MAP: Record<TabId, string> = useMemo(() => ({
    general: t('tabs.general'),
    models: t('tabs.models'),
    skills: t('tabs.skills'),
    connections: t('tabs.connections'),
    knowledge: t('tabs.knowledgeBase'),
    guardrails: t('tabs.guardrails'),
    access: t('tabs.studioAccess'),
  }), [t])

  const TABS: { id: TabId; label: string; previewBadge?: boolean; hidden?: boolean }[] = useMemo(() =>
    TAB_IDS
      .filter(id => !(id === 'general' && !FEATURES.globalSettingsGeneral))
      .filter(id => !isGlobalTabKey(id) || canOpenGlobalTab(id))
      .map(id => ({
        id,
        label: TAB_LABEL_MAP[id],
        previewBadge:
          (id === 'connections' && !FEATURES.globalConnectionsConfig) ||
          (id === 'skills' && !FEATURES.globalSkillsConfig) ||
          (id === 'knowledge' && !FEATURES.globalKnowledgeConfig) ||
          (id === 'guardrails' && !FEATURES.globalGuardrailsConfig),
        /* Models is Admin-only data but deliberately absent from
           GLOBAL_TAB_ACCESS (see lib/roleAccess.ts), so the role filter above
           leaves it in. Hide it from a non-Admin here — its panel body is gated
           on `isGlobalAdmin` below, so the button would otherwise open a blank
           panel and a Save that 403s on PUT /global-settings/models. */
        hidden: id === 'models' && (!FEATURES.globalSettingsModels || !isGlobalAdmin),
      })),
    [TAB_LABEL_MAP, canOpenGlobalTab, isGlobalAdmin]
  )

  /* ─── Fetch models catalog ─── */
  const modelsQuery = useQuery({
    queryKey: ['models', 'catalog'],
    queryFn: () => apiFetch<ModelConfigurationListResponse>('/api/v1/models'),
  })

  /* ─── Initialise form when settings load ─── */
  useMemo(() => {
    if (settingsQuery.data && !form) {
      const f = initForm(settingsQuery.data)
      setForm(f)
      setSavedForm(f)
    }
  }, [settingsQuery.data, form])

  /* ─── Save mutation ───
     One PUT to the active tab's own endpoint (MS-741 §3.1/§4.7), not the whole
     form to the umbrella endpoint — a Save never carries more than one tab's
     changes, since the page already refuses a tab switch while dirty. */
  const saveMutation = useMutation({
    mutationFn: ({ tabId, payload }: { tabId: TabId; payload: Record<string, unknown> }) => {
      const endpoint = TAB_SAVE_ENDPOINT[tabId]
      if (!endpoint) throw new Error(`No save endpoint for tab "${tabId}"`)
      return apiFetch<GlobalSettingsResponse>(endpoint, { method: 'PUT', body: JSON.stringify(payload) })
    },
    onSuccess: (data, { tabId }) => {
      // Merge, never re-init — see mergeTabSaveResponse.
      setForm(prev => prev ? mergeTabSaveResponse(prev, tabId, data) : initForm(data))
      setSavedForm(prev => prev ? mergeTabSaveResponse(prev, tabId, data) : initForm(data))
      // Drop the pre-save GET snapshot: without this, a remount of this page
      // (left-nav → Global Settings) re-renders the stale cache entry for the
      // app-wide `staleTime` window. Invalidate rather than seeding the cache
      // with `data` — a per-tab PUT response carries only the section it saved.
      qc.invalidateQueries({ queryKey: GLOBAL_SETTINGS_QUERY_KEY })
      // A Models-tab save changes the catalog other pages read under their own
      // keys (`['models']` in AgentDetail, `['models', 'catalog']` here).
      if (tabId === 'models') qc.invalidateQueries({ queryKey: ['models'] })
      setSaveConfirm(false)
      if (tabToSwitchAfterSave) {
        commitTab(tabToSwitchAfterSave)
        setTabToSwitchAfterSave(null)
      }
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : t('globalSettings.errors.failedToSave')
      window.alert(message)
      setSaveConfirm(false)
    },
  })

  const dirty = form && savedForm ? isDirty(form, savedForm) : false
  const catalog = modelsQuery.data?.items ?? []

  const { setDirty: publishDirty, clearDirty } = useDirty('global-settings')
  useEffect(() => { publishDirty(dirty) }, [dirty, publishDirty])

  /* ── In-app navigation guard (left-nav, breadcrumb, brand click) — consolidated
     hook checks page-level dirty AND the entity-editor registry. ── */
  const { dialogOpen: navBlockDialog, confirmLeave: confirmNavLeave, cancelLeave: cancelNavLeave } = usePageNavigationGuard(dirty, clearDirty)

  function patchForm(patch: Partial<GlobalFormState>) {
    setForm(f => f ? { ...f, ...patch } : f)
  }

  function handleDiscard() {
    if (savedForm) setForm({ ...savedForm })
  }

  function handleSaveClick() {
    if (!form || !savedForm) return
    // Model validity only gates a Save of the Models tab itself now that a Save
    // is per-tab (MS-741 §4.7) — checking it unconditionally would block an
    // Editor's Skills/Knowledge Base save against `form.models`, which is only a
    // type-safe placeholder for a caller who can't see that Admin-only section.
    if (activeTab === 'models') {
      const modelErrKey = validateModels(form.models)
      if (modelErrKey) { window.alert(t(modelErrKey)); return }
    }
    const risky = detectRiskyChanges(savedForm, form)
    if (hasRiskyChanges(risky)) { setSaveConfirm(true); return }
    commitSave()
  }

  function commitSave() {
    if (!form) return
    saveMutation.mutate({ tabId: activeTab, payload: buildTabSavePayload(activeTab, form) })
  }

  /* ─── Entity CRUD callbacks — sync both form + saved ─── */

  /* Entity CRUD hits its own endpoint, so the umbrella global-settings cache
     entry is stale afterwards even though `form` is patched in place. */
  const invalidateSettingsCache = useCallback(() => {
    qc.invalidateQueries({ queryKey: GLOBAL_SETTINGS_QUERY_KEY })
  }, [qc])

  const handleSkillAdded = useCallback((skillId: string) => {
    const binding: SkillRef = { skillId, isEnabled: true }
    setForm(f => f ? { ...f, skills: [...f.skills.filter(s => s.skillId !== skillId), binding] } : f)
    setSavedForm(f => f ? { ...f, skills: [...f.skills.filter(s => s.skillId !== skillId), binding] } : f)
    invalidateSettingsCache()
  }, [invalidateSettingsCache])

  const handleSkillRemoved = useCallback((skillId: string) => {
    setForm(f => f ? { ...f, skills: f.skills.filter(s => s.skillId !== skillId) } : f)
    setSavedForm(f => f ? { ...f, skills: f.skills.filter(s => s.skillId !== skillId) } : f)
    invalidateSettingsCache()
  }, [invalidateSettingsCache])

  const handleConnectionAdded = useCallback((binding: ConnectionBindingRef) => {
    setForm(f => f ? { ...f, connections: [...f.connections.filter(c => c.connectionId !== binding.connectionId), binding] } : f)
    setSavedForm(f => f ? { ...f, connections: [...f.connections.filter(c => c.connectionId !== binding.connectionId), binding] } : f)
    invalidateSettingsCache()
  }, [invalidateSettingsCache])

  const handleConnectionRemoved = useCallback((connId: string) => {
    setForm(f => f ? { ...f, connections: f.connections.filter(c => c.connectionId !== connId) } : f)
    setSavedForm(f => f ? { ...f, connections: f.connections.filter(c => c.connectionId !== connId) } : f)
    invalidateSettingsCache()
  }, [invalidateSettingsCache])

  const handleKbAdded = useCallback((kbId: string) => {
    const binding: KnowledgeSourceRef = { knowledgeSourceId: kbId, isEnabled: true }
    setForm(f => f ? { ...f, knowledgeSources: [...f.knowledgeSources.filter(k => k.knowledgeSourceId !== kbId), binding] } : f)
    setSavedForm(f => f ? { ...f, knowledgeSources: [...f.knowledgeSources.filter(k => k.knowledgeSourceId !== kbId), binding] } : f)
    invalidateSettingsCache()
  }, [invalidateSettingsCache])

  const handleKbRemoved = useCallback((kbId: string) => {
    setForm(f => f ? { ...f, knowledgeSources: f.knowledgeSources.filter(k => k.knowledgeSourceId !== kbId) } : f)
    setSavedForm(f => f ? { ...f, knowledgeSources: f.knowledgeSources.filter(k => k.knowledgeSourceId !== kbId) } : f)
    invalidateSettingsCache()
  }, [invalidateSettingsCache])

  /* ─── Risky changes summary (for confirmation dialog) ─── */

  const riskyChanges = form && savedForm ? detectRiskyChanges(savedForm, form) : null

  /* ─── Loading / error states ─── */

  if (settingsQuery.isLoading) {
    return (
      <div className="page-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner />
      </div>
    )
  }

  if (settingsQuery.isError) {
    return (
      <div className="page-body">
        <div role="alert" style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: 'var(--danger)' }}>
          {t('globalSettings.errors.failedToLoad')} {settingsQuery.error instanceof Error ? settingsQuery.error.message : ''}
        </div>
      </div>
    )
  }

  if (!form || !savedForm) return null

  return (
    <div className="page-view" style={{ padding: '16px 32px 0', '--editor-card-max': 'calc(100vh - 231px)' } as React.CSSProperties}>
      <PageHeader
        title={t('globalSettings.heading')}
        caption={t('globalSettings.subtitle')}
        actions={
          <PageSaveDiscardActions
            dirty={dirty}
            saving={saveMutation.isPending}
            onSave={handleSaveClick}
            onDiscard={handleDiscard}
            saveLabel={t('globalSettings.save')}
            discardLabel={t('globalSettings.discard')}
          />
        }
      />

      {/* Tab navigation */}
      <div className="tabs">
        {TABS.map(tab => (
          <GlobalSettingsTabButton
            key={tab.id}
            tab={tab}
            isActive={activeTab === tab.id}
            dirty={dirty}
            onCommit={commitTab}
            onEntityConflict={(tabId) => { setPendingTab(tabId); setEntityTabSwitchConfirm(true) }}
            onPageConflict={(tabId) => { setPendingTab(tabId); setTabSwitchDialog(true) }}
            previewTooltip={t('common.previewTooltip')}
            previewLabel={t('common.preview')}
          />
        ))}
      </div>

      <div className="tab-scroll-area" style={{ paddingLeft: 0 }}>
      {/* Tab panels */}
      {/* Models is Admin-only data (GET withholds it as `null` from an Editor —
          MS-741 §3.1), independent of GLOBAL_TAB_ACCESS (it's deliberately absent
          from that map — see lib/roleAccess.ts). Gate on isGlobalAdmin directly
          rather than a role-matrix entry that doesn't exist for it. */}
      <div className={tabPanelClass('models', activeTab)} id="tab-global-models" role="tabpanel">
        {isGlobalAdmin && (
          <ModelsTab
            models={form.models}
            catalog={catalog}
            onChange={(models) => patchForm({ models })}
          />
        )}
      </div>

      {/* Connections is Admin-only per GLOBAL_TAB_ACCESS, matching the backend's
          GET scoping — gate the whole panel so an Editor (for whom
          `form.connections` is a placeholder, never the real data) never mounts
          it, not just hide it from the tab bar. */}
      <div className={tabPanelClass('connections', activeTab)} id="tab-global-connections" role="tabpanel">
        {canOpenGlobalTab('connections') && (
          <GlobalConnectionsPanel
            agentsContracted={agentsContracted}
            connectionBindings={form.connections}
            savedBindings={savedForm.connections}
            onChange={(connections) => patchForm({ connections })}
            onAdded={handleConnectionAdded}
            onRemoved={handleConnectionRemoved}
          />
        )}
      </div>

      <div className={tabPanelClass('knowledge', activeTab)} id="tab-global-knowledge" role="tabpanel">
        <GlobalKnowledgePanel
          agentsContracted={agentsContracted}
          onAdded={handleKbAdded}
          onRemoved={handleKbRemoved}
        />
      </div>

      <div className={tabPanelClass('skills', activeTab)} id="tab-global-skills" role="tabpanel">
        <GlobalSkillsPanel
          agentsContracted={agentsContracted}
          skillBindings={form.skills}
          savedBindings={savedForm.skills}
          onToggle={(skillId, isEnabled) => patchForm({ skills: form.skills.map(s => s.skillId === skillId ? { ...s, isEnabled } : s) })}
          onAdded={handleSkillAdded}
          onRemoved={handleSkillRemoved}
        />
      </div>

      <div className={tabPanelClass('guardrails', activeTab)} id="tab-global-guardrails" role="tabpanel">
        {canOpenGlobalTab('guardrails') && (
          <GlobalGuardrailsPanel
            agentsContracted={agentsContracted}
            form={form.guardrails}
            onChange={(patch) => patchForm({ guardrails: { ...form.guardrails, ...patch } })}
          />
        )}
      </div>

      <div className={tabPanelClass('access', activeTab)} id="tab-global-access" role="tabpanel">
        {canOpenGlobalTab('access') && (
          <>
            <RoleMatrixCard />
            <GlobalAccessCard
              access={form.access}
              onChange={(access) => patchForm({ access })}
              agentsContracted={agentsContracted}
            />
          </>
        )}
      </div>

      </div>{/* end .tab-scroll-area */}

      {/* Risky changes confirmation */}
      {saveConfirm && riskyChanges && (
        <ConfirmDialog
          title={t('globalSettings.riskyChanges.title')}
          message={t('globalSettings.riskyChanges.message', { details: buildRiskyMessage(t, riskyChanges) })}
          confirmLabel={t('globalSettings.riskyChanges.confirm')}
          danger
          onConfirm={commitSave}
          onCancel={() => { setSaveConfirm(false); setTabToSwitchAfterSave(null) }}
        />
      )}

      {/* Navigation guard */}
      {navBlockDialog && (
        <UnsavedChangesDialog onConfirm={confirmNavLeave} onCancel={cancelNavLeave} />
      )}

      {/* Tab-switch guard, entity-editor case — fires when an open Global Skill/
          Connection/Knowledge Base source editor has unsaved changes; discarding
          here only clears the open editor's own draft. */}
      {entityTabSwitchConfirm && pendingTab && (
        <UnsavedChangesDialog
          onConfirm={() => {
            markAllEntityEditorsClean()
            setEntityTabSwitchConfirm(false)
            commitTab(pendingTab)
            setPendingTab(null)
          }}
          onCancel={() => {
            setEntityTabSwitchConfirm(false)
            setPendingTab(null)
          }}
        />
      )}

      {/* Tab-switch guard, page-level case — fires when a plain field/toggle change
          (not routed through an entity editor) has dirtied the page form. */}
      {tabSwitchDialog && pendingTab && (
        <UnsavedChangesDialog
          onConfirm={() => {
            handleDiscard()
            setTabSwitchDialog(false)
            commitTab(pendingTab)
            setPendingTab(null)
          }}
          onCancel={() => { setTabSwitchDialog(false); setPendingTab(null) }}
        />
      )}
    </div>
  )
}
